
import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";

export interface ComboboxOption {
  id: number | string;
  label: string;
  sublabel?: string;
}

interface ComboboxProps {
  /** Current text shown in the input (kept in sync by the parent). */
  inputValue: string;

  /** Fired on every keystroke so the parent can keep free-text editing working. */
  onInputChange: (value: string) => void;

  /** Fired when the user picks an option from the dropdown. */
  onSelectOption: (option: ComboboxOption) => void;

  /** Async lookup, called (debounced) whenever the query changes and on focus. */
  fetchOptions: (query: string) => Promise<ComboboxOption[]>;

  placeholder?: string;
  className?: string;

  /** Extra class applied to the wrapping <div>. */
  wrapperClassName?: string;

  disabled?: boolean;

  "aria-label"?: string;

  inputRef?: (el: HTMLInputElement | null) => void;

  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Lightweight search-as-you-type dropdown.
 *
 * Behavior:
 * - Opens when input is focused.
 * - Searches when input changes.
 * - Dropdown re-opens when user types after selecting an option.
 * - Shows "No matches" when search returns no result.
 * - Keeps free-text input behavior.
 */
export default function Combobox({
  inputValue,
  onInputChange,
  onSelectOption,
  fetchOptions,
  placeholder,
  className,
  wrapperClassName,
  disabled,
  inputRef,
  onKeyDown,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ComboboxOption[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const requestIdRef = useRef(0);

  // =========================
  // Run Search
  // =========================

  const runFetch = (query: string) => {
    const requestId = ++requestIdRef.current;

    setLoading(true);

    fetchOptions(query)
      .then((results) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setOptions(results);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setOptions([]);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setLoading(false);
      });
  };

  // =========================
  // Debounced Search
  // =========================

  useEffect(() => {
    if (!open) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      runFetch(inputValue);
    }, 250);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };

    // fetchOptions intentionally excluded.
    // Parent may recreate fetchOptions on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, open]);

  // =========================
  // Cleanup
  // =========================

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // =========================
  // Focus
  // =========================

  const handleFocus = () => {
    if (disabled) {
      return;
    }

    // Cancel pending blur close.
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    setOpen(true);

    runFetch(inputValue);
  };

  // =========================
  // Input Change
  // =========================

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Important:
    // Re-open dropdown whenever user starts typing again.
    //
    // This fixes:
    // Product selected
    // → product removed
    // → type again
    // → dropdown appears again
    if (!disabled) {
      setOpen(true);
    }

    onInputChange(value);
  };

  // =========================
  // Blur
  // =========================

  const handleBlur = () => {
    // Delay closing so a click on an option registers before
    // the list is hidden.
    blurTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  // =========================
  // Select Option
  // =========================

  const handleSelect = (option: ComboboxOption) => {
    // Cancel blur timeout because we're handling selection ourselves.
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    onSelectOption(option);

    setOpen(false);
  };

  return (
    <div
      className={`relative ${wrapperClassName ?? ""}`}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        {...aria}
      />

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
              <Loader2
                size={13}
                className="animate-spin"
              />
              Searching...
            </div>
          )}

          {/* No Result */}
          {!loading && options.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
              <Search size={13} />
              No matches
            </div>
          )}

          {/* Options */}
          {!loading &&
            options.map((option) => (
              <button
                type="button"
                key={option.id}
                // Use onMouseDown instead of onClick.
                // This fires before input blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-blue-50"
              >
                <span className="font-medium text-gray-800">
                  {option.label}
                </span>

                {option.sublabel && (
                  <span className="text-xs text-gray-400">
                    {option.sublabel}
                  </span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

